<script setup lang="ts">
import SvgIcon from "../SvgIcon.vue";
import SvgButton from "./SvgButton.vue";
import Tooltip from "~/components/Common/Tooltip.vue";

export interface IActions {
  name: string;
  icon: string;
  function: Function;
  disabled?: boolean;
  tooltip?: string;
}

const props = defineProps({
  name: {
    type: String,
  },
  icon: {
    type: String,
    default: "panel",
  },
  actions: {
    type: Object as PropType<IActions[]>,
  },
  id: { type: [String, Number] },
  positionLeft: {
    type: Number,
    default: 0,
  },
  positionTop: {
    type: Number,
    default: 0,
  },
  inHeader: {
    type: Boolean,
  },
  onSelect: {
    type: Boolean,
  },
  dotStyle: {
    type: Boolean,
    default: false,
  },
  disable: {
    type: Boolean,
    default: false,
  },
  forceClose: {
    type: Boolean,
  },
  loader: {
    type: Boolean,
    default: false,
  },
  buttonTooltip: {
    type: String,
    default: "",
  },
  positionTooltip: {
    type: String,
    default: "top-mid",
  },
});

const emit = defineEmits(["forceCloseFn"]);

const slots = defineSlots(); // Ensure slots are defined

const hasDefaultSlot = computed(() => !!slots.default);

watch(
  () => props.forceClose,
  (value) => {
    if (value) {
      isPopoverOpen.value = false;
      isPopoverContentVisible.value = false;

      emit("forceCloseFn", false);
    }
  },
);

const isPopoverOpen = ref(false);
const buttonRef = ref();
const popoverRef = ref();
const isPopoverContentVisible = ref(false);

async function togglePopover() {
  isPopoverOpen.value = !isPopoverOpen.value;
  isPopoverContentVisible.value = true;

  await nextTick();

  if (isPopoverOpen.value) {
    const buttonElement = buttonRef.value?.$el || buttonRef.value;
    const popoverElement = popoverRef.value?.$el || popoverRef.value;

    const buttonRect = buttonElement.getBoundingClientRect();
    const popoverRect = popoverElement.getBoundingClientRect();

    if (props.inHeader) {
      popoverContentLeft.value =
        props.positionLeft !== 0 ? buttonRect.left - (popoverRect.width + props.positionLeft) : buttonRect.left;
      popoverContentTop.value = buttonRect.top + (buttonRect.height + props.positionTop) + 8; // magic value for esthetic
    } else {
      popoverContentLeft.value = buttonRect.left - (popoverRect.width + props.positionLeft);
      popoverContentTop.value = buttonRect.top - (buttonRect.height + props.positionTop);
    }

    const margin = 16;
    const maxLeft = window.innerWidth - popoverRect.width - margin;
    popoverContentLeft.value = Math.max(margin, Math.min(popoverContentLeft.value, maxLeft));
  }
}

const popoverContentLeft = ref();
const popoverContentTop = ref();

function closePopover(event: { target: any }) {
  const buttonElement = buttonRef.value?.$el || buttonRef.value;
  const popoverElement = popoverRef.value?.$el || popoverRef.value;

  const isInsideButton = buttonElement && buttonElement.contains(event.target);
  const isInsidePopover = hasDefaultSlot.value && popoverElement && popoverElement.contains(event.target);

  if (isPopoverContentVisible.value && event.target && !isInsideButton && !isInsidePopover) {
    isPopoverContentVisible.value = false;
    isPopoverOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", closePopover);
});

onUnmounted(() => {
  document.removeEventListener("click", closePopover);
});
</script>

<template>
  <div
    class="relative"
    :class="{ 'is-popover-select panelButtonActions': isPopoverOpen && dotStyle, panelButtonActions: onSelect }"
  >
    <Tooltip v-if="!disable && buttonTooltip && !isPopoverOpen" :position="positionTooltip" minified>
      <template #img>
        <SvgButton
          ref="buttonRef"
          :loader="loader"
          :class="{
            'h-[24px] w-[24px]': inHeader,
            'svg-button svg-basic cursor-pointer !border-none': !inHeader && icon !== 'edit',
            'icon-svg-upload': icon === 'upload',
            'edit-svg  svg-basic cursor-pointer !border-none': icon === 'edit',
            'popover-content-on-select': onSelect,
          }"
          :radius="8"
          :name="icon"
          @click.stop.prevent="togglePopover"
        ></SvgButton>
      </template>
      <template #content>
        <div class="w-fit whitespace-nowrap">
          <span class="meta">{{ buttonTooltip }}</span>
        </div>
      </template>
    </Tooltip>
    <SvgButton
      v-else-if="!disable"
      ref="buttonRef"
      :loader="loader"
      :class="{
        'h-[24px] w-[24px]': inHeader,
        'svg-button svg-basic cursor-pointer !border-none': !inHeader && icon !== 'edit',
        'icon-svg-upload': icon === 'upload',
        'edit-svg  svg-basic cursor-pointer !border-none': icon === 'edit',
        'popover-content-on-select': onSelect,
      }"
      :radius="8"
      :name="icon"
      @click.stop.prevent="togglePopover"
    ></SvgButton>
    <Teleport v-if="isPopoverOpen" to="body">
      <div
        v-if="isPopoverContentVisible"
        ref="popoverRef"
        :class="{
          'popover-content': !hasDefaultSlot,
          'popover-content__with-default-slot': hasDefaultSlot,
        }"
        :style="{
          left: `${popoverContentLeft}px`,
          top: `${popoverContentTop}px`,
        }"
      >
        <template v-if="hasDefaultSlot">
          <slot></slot>
        </template>

        <ul v-else class="w-full">
          <li v-for="(action, i) in actions" :key="i">
            <Tooltip v-if="action.disabled && action.tooltip" position="top-mid" append-to-body minified>
              <template #img>
                <div class="flex items-center opacity-50">
                  <SvgIcon
                    :name="action.icon"
                    :class="{
                      'svg-basic icon-svg': icon !== 'upload',
                      'svg-basic icon-svg-upload': icon === 'upload',
                    }"
                  ></SvgIcon>
                  <span class="body_regular ml-2 text-secondary-color">{{ action.name }}</span>
                </div>
              </template>
              <template #content>
                <span class="meta whitespace-pre-line">{{ action.tooltip }}</span>
              </template>
            </Tooltip>
            <div v-else-if="actions" class="flex items-center" @click="action.function(id)">
              <SvgIcon
                :name="action.icon"
                :class="{
                  'svg-basic icon-svg': icon !== 'upload',
                  'svg-basic icon-svg-upload': icon === 'upload',
                }"
              ></SvgIcon>
              <span class="body_regular ml-2 text-secondary-color hover:text-primary-blue-color">{{
                action.name
              }}</span>
            </div>
          </li>
        </ul>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.svg-button {
  display: flex;
  flex-direction: column;
}

.popover-content-on-select {
  @apply my-1;
  color: var(--text-secondary-color) !important;

  &:hover {
    color: var(--text-secondary-color);
    background: var(--grey-lightest);
  }
}

.svg-basic {
  width: 14px;
  height: 14px;
  min-width: 14px;
  min-height: 14px;
  max-width: 14px;
  max-height: 14px;
}

.icon-svg {
  color: var(--primary-blue-color);
}

.edit-svg {
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  max-width: 16px;
  max-height: 16px;
  cursor: pointer;
}

.icon-svg-upload {
  color: var(--green-main);
  border-color: var(--green-main);

  &:hover {
    color: var(--green-main);
    border-color: var(--green-main);
  }
}

.popover-content {
  position: absolute;
  list-style-type: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 8px;
  gap: 4px;
  width: fit-content;
  /* background/main */
  background: var(--white-main);
  /* standard-shadow */
  box-shadow: var(--shadow-medium);
  border-radius: 6px;
  z-index: 501;
}

.popover-content__with-default-slot {
  position: absolute;
  list-style-type: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: fit-content;
  /* background/main */
  background: var(--white-main);
  /* standard-shadow */
  box-shadow: var(--shadow-medium);
  border-radius: 6px;
  z-index: 501;
}

.popover-content li {
  padding: 5px;
  cursor: pointer;
}

.panelButtonActions {
  @apply flex items-center justify-center w-6 h-6 rounded relative;

  &:hover {
    @apply cursor-pointer;
    background-color: var(--grey-lightest);
  }
}

.is-popover-select {
  @apply cursor-pointer;
  background-color: var(--grey-lightest);
}
</style>
